import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

interface ProtectedRouteStableProps {
  allowedRoles?: string[];
}

const ProtectedRouteStable: React.FC<ProtectedRouteStableProps> = ({ allowedRoles }) => {
  const { member, isAuthenticated, isLoading } = useAuth();
  
  // Cache the initial auth state to prevent re-checks
  const [initialAuthState, setInitialAuthState] = React.useState<boolean | null>(null);
  const [hasCheckedAuth, setHasCheckedAuth] = React.useState(false);

  React.useEffect(() => {
    // Give auth context time to load from storage
    const timer = setTimeout(() => {
      setHasCheckedAuth(true);
      // Lock in the auth state after initial check
      if (initialAuthState === null) {
        setInitialAuthState(isAuthenticated);
        console.log('[ProtectedRouteStable] Locked auth state:', isAuthenticated);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [isAuthenticated, initialAuthState]);

  if (isLoading || !hasCheckedAuth) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Use the cached auth state instead of live state
  const isAuthValid = initialAuthState !== null ? initialAuthState : isAuthenticated;
  
  if (!isAuthValid) {
    // Double-check tokens exist before redirecting
    const hasToken = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    if (hasToken) {
      console.log('[ProtectedRouteStable] Has token but not authenticated, using cached state');
      // Don't redirect if we have a token
      return <Outlet />;
    }
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && member && !allowedRoles.includes(member.memberType)) {
    return <Navigate to="/voicemail" replace />;
  }

  return <Outlet />;
};

export default ProtectedRouteStable;